import { useMemo, useState } from "react";
import { exploreItems, allTags } from "@/mocks/manga";

export default function ExploreSection() {
  const [query, setQuery] = useState("");
  const [activeTags, setActiveTags] = useState<string[]>([]);

  const toggleTag = (tag: string) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return exploreItems.filter((item) => {
      const matchTags =
        activeTags.length === 0 ||
        activeTags.some((t) => item.tags.includes(t));
      const matchKeyword =
        keyword === "" ||
        item.title.toLowerCase().includes(keyword) ||
        item.author.toLowerCase().includes(keyword) ||
        item.category.toLowerCase().includes(keyword) ||
        item.tags.some((t) => t.toLowerCase().includes(keyword));
      return matchTags && matchKeyword;
    });
  }, [query, activeTags]);

  const reset = () => {
    setQuery("");
    setActiveTags([]);
  };

  return (
    <section id="explore" className="w-full bg-background-50 py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-label text-secondary-700 uppercase tracking-widest">
              <i className="ri-filter-3-line text-base" aria-hidden="true"></i>
              Filter &amp; Search
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              按题材筛选，快速锁定心头好
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              下面这些作品都来自本页榜单与新番。点选你偏好的题材标签，
              或在搜索框输入作品名、作者，结果会立刻刷新，全程不离开本页。
            </p>
          </div>

          <div className="relative w-full md:w-72 shrink-0">
            <i
              className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
              aria-hidden="true"
            ></i>
            <label htmlFor="explore-search" className="sr-only">
              搜索作品或作者
            </label>
            <input
              id="explore-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索作品名、作者或题材"
              className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-2">
          <span className="text-xs font-label text-foreground-500 mr-1">热门题材：</span>
          {allTags.map((tag) => {
            const isActive = activeTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                aria-pressed={isActive}
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-label border transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-100 border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                #{tag}
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-label text-foreground-600">
            共找到 <span className="font-bold text-primary-600">{results.length}</span> 部作品
            {activeTags.length > 0 && (
              <span className="text-foreground-500">
                {" "}
                · 已选 {activeTags.length} 个题材
              </span>
            )}
          </p>
          {(activeTags.length > 0 || query !== "") && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm font-label text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true"></i>
              清除筛选
            </button>
          )}
        </div>

        {results.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {results.map((item) => (
              <article
                key={item.title}
                className="flex gap-4 p-4 rounded-lg bg-background-100/70 border border-background-200 hover:border-primary-300 transition-colors"
              >
                <div className="w-20 h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title} 漫画封面`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <span className="font-heading text-sm font-bold text-primary-600 shrink-0">
                      {item.score}
                    </span>
                  </div>
                  <p className="mt-1 text-xs font-label text-foreground-500">
                    {item.author} · {item.category}
                  </p>
                  <span
                    className={`mt-2 inline-block px-2 py-0.5 rounded-full text-xs font-label ${
                      item.status === "已完结"
                        ? "bg-accent-100 text-accent-800"
                        : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    {item.status}
                  </span>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-md bg-background-200/80 text-xs font-label text-foreground-700"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center justify-center py-16 rounded-lg bg-background-100 border border-dashed border-background-300 text-center">
            <span className="w-12 h-12 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">
              没有找到同时符合这些条件的作品，试试减少几个题材标签。
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              清除全部筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}